import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  obterGridContratos,
  obterDetalheContrato,
  listarEmpreendimentosContrato,
  listarImoveisContrato,
  listarGruposEmpresaContrato,
  listarContasPorGrupoEmpresaContrato,
  autoPreencheClienteContrato,
} from '~/Services/Juridico/ControleContratoService';

export const extrairMensagemErro = (err) =>
  (err && err.message) ||
  'Erro ao processar. Tente novamente.';

const FILTRO_INICIAL = {
  codigoGrupoEmpresa: null,
  codigoEmpreendimento: null,
  codigoImovel: null,
  codigoConta: null,
  dataIni: null,
  dataFim: null,
  status: null,
  boleto: null,
  pagina: 1,
  tamanhoPagina: 50,
  ordenarPor: 'data',
  direcao: 'desc',
};

const useControleContrato = () => {
  const { search } = useLocation();

  // Auth
  const caracteristicas = useSelector((state) => state.auth.caracteristicas);

  // Listas de combos
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [contas, setContas] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [imoveis, setImoveis] = useState([]);

  // Grid — backend retorna lista completa; paginação/ordenação são client-side.
  const [contratosBruto, setContratosBruto] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pesquisado, setPesquisado] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState(FILTRO_INICIAL);

  // Seleção de linha para ações
  const [contrato, setContrato] = useState(null);

  // Detalhe do contrato selecionado (cabeçalho + itens + pendências)
  const [detalhe, setDetalhe] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);

  // Contexto externo (deep link de Gerência — ADR-004)
  const [contextoExterno, setContextoExterno] = useState(false);

  // Modais
  const [modalIncluir, setModalIncluir] = useState(false);
  const [modalMudarFase, setModalMudarFase] = useState(false);
  const [modalAlterarItem, setModalAlterarItem] = useState(false);
  const [modalAnexar, setModalAnexar] = useState(false);
  const [modalAcoes, setModalAcoes] = useState(false);

  // Deep link ?contexto=gerencia&boleto=N (ADR-004)
  useEffect(() => {
    const params = new URLSearchParams(search);
    const contexto = params.get('contexto');
    const boletoParam = params.get('boleto');
    if (contexto === 'gerencia' && boletoParam) {
      const boletoNum = Number(boletoParam);
      setContextoExterno(true);
      const filtrosDeepLink = { ...FILTRO_INICIAL, boleto: boletoNum };
      setFiltros(filtrosDeepLink);
      buscar(filtrosDeepLink);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Carrega Grupos Empresa do usuario (uma vez)
  useEffect(() => {
    let ativo = true;
    listarGruposEmpresaContrato()
      .then((r) => {
        if (!ativo) return;
        setGruposEmpresa(r || []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Cascade GE → Contas (anti-race)
  useEffect(() => {
    setContas([]);
    const ge = filtros.codigoGrupoEmpresa;
    if (!ge) return;
    let ativo = true;
    listarContasPorGrupoEmpresaContrato(ge)
      .then((r) => {
        if (!ativo) return;
        setContas(r || []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, [filtros.codigoGrupoEmpresa]);

  // Cascade Empreendimento → Imóvel (anti-race W-0510 L3)
  // Empreendimento e Família são 1:1 no schema (mesma PK Codigo_Familia).
  useEffect(() => {
    setImoveis([]);
    const emp = filtros.codigoEmpreendimento;
    if (!emp) return;
    let ativo = true;
    listarImoveisContrato(emp)
      .then((r) => {
        if (!ativo) return;
        setImoveis(r || []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, [filtros.codigoEmpreendimento]);

  const carregarEmpreendimentos = useCallback(async (ge) => {
    if (!ge) { setEmpreendimentos([]); return; }
    try {
      const r = await listarEmpreendimentosContrato(ge);
      setEmpreendimentos(r || []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  }, []);

  const buscar = useCallback(async (filtrosParam) => {
    const f = filtrosParam || filtros;
    setLoading(true);
    try {
      const r = await obterGridContratos(f);
      setContratosBruto(r.itens || []);
      setPesquisado(true);
      setContrato(null);
      setDetalhe(null);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  // Ordenação + paginação client-side
  const total = contratosBruto.length;
  const contratos = useMemo(() => {
    const { ordenarPor, direcao, pagina, tamanhoPagina } = filtros;
    const sorted = [...contratosBruto].sort((a, b) => {
      const va = a[ordenarPor];
      const vb = b[ordenarPor];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return direcao === 'desc' ? -cmp : cmp;
    });
    const inicio = (pagina - 1) * tamanhoPagina;
    return sorted.slice(inicio, inicio + tamanhoPagina);
  }, [contratosBruto, filtros]);

  const carregarDetalhe = useCallback(async (row) => {
    setContrato(row);
    setDetalhe(null);
    setLoadingDetalhe(true);
    try {
      const r = await obterDetalheContrato(
        row.codigo_grupo_empresa,
        row.conta,
        row.boleto
      );
      setDetalhe(r);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingDetalhe(false);
    }
  }, []);

  const atualizarFiltro = (campo, valor) => {
    setFiltros((prev) => {
      const next = { ...prev, [campo]: valor, pagina: 1 };
      // cascata — limpar dependentes quando pai muda
      if (campo === 'codigoGrupoEmpresa') {
        next.codigoEmpreendimento = null;
        next.codigoImovel = null;
        next.codigoConta = null;
      }
      if (campo === 'codigoEmpreendimento') {
        next.codigoImovel = null;
      }
      return next;
    });
    if (campo === 'codigoGrupoEmpresa') carregarEmpreendimentos(valor);
  };

  // Paginação / ordenação são client-side — não dispara fetch.
  const mudarPagina = (pagina) => {
    setFiltros((prev) => ({ ...prev, pagina }));
  };

  const mudarOrdenacao = (campo) => {
    setFiltros((prev) => {
      const novaDirecao =
        prev.ordenarPor === campo && prev.direcao === 'asc' ? 'desc' : 'asc';
      return { ...prev, ordenarPor: campo, direcao: novaDirecao, pagina: 1 };
    });
  };

  const limparFiltros = () => {
    setFiltros(FILTRO_INICIAL);
    setContratosBruto([]);
    setPesquisado(false);
    setContrato(null);
    setDetalhe(null);
    setContextoExterno(false);
  };

  return {
    // estado
    filtros,
    gruposEmpresa,
    contas,
    empreendimentos,
    imoveis,
    contratos,
    total,
    loading,
    pesquisado,
    contrato,
    detalhe,
    loadingDetalhe,
    contextoExterno,
    caracteristicas,
    // modais
    modalIncluir,
    modalMudarFase,
    modalAlterarItem,
    modalAnexar,
    modalAcoes,
    setModalIncluir,
    setModalMudarFase,
    setModalAlterarItem,
    setModalAnexar,
    setModalAcoes,
    // ações
    atualizarFiltro,
    buscar,
    mudarPagina,
    mudarOrdenacao,
    limparFiltros,
    carregarDetalhe,
    setContrato,
    setDetalhe,
    setContratos: setContratosBruto,
  };
};

export default useControleContrato;
